import React, { useState, Suspense, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { useRouter } from "next/router";
import styled, { withTheme } from "styled-components";
import Image from "next/image";
import { Form, Field, FormSpy } from "react-final-form";
import { get, isObject, debounce } from "lodash";
import TextInput from "fields/TextInput";
import Button from "components/Button";
import MagIcon from "components/icons/magglass";
import LocationIcon from "components/icons/location";
import StorefrontIcon from "components/icons/storefront";
import Dropdown from "fields/SelectDropdown";
import { AddressDropdown } from "dynamic";
import useCurrentLocation, {
  useLocationChange,
} from "hooks/use-current-location";
import { parseDateToMsecs } from "lib/date-helpers";
import useVendorClient from "hooks/use-vendor-client";
import SmallBusinessCard from "components/SmallBusinessCard";
import { BASE_API_URL } from "lib/constants";
import useOutsideClickHider from "components/helpers/OutsideClickHider";
import { DateTime } from "luxon";

export const OuterWrapper = styled.section`
  background-color: ${({ theme }) => theme.themeColors.lightGreen1};
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 4rem 1.125rem 1.5rem;
  width: 100%;

  ${({ theme }) => theme.mq.md`
    padding: 5rem 1.75rem 2rem;
  `}

  ${({ theme }) => theme.mq.lg`
    align-items: center;
    flex-direction: row;
    padding: 3rem 1.5rem 5rem 1.5rem;
  `}

  ${({ theme }) => theme.mq.xxl`
    padding: 2.375rem 1.5rem 4.5rem 6.25rem;
  `}
`;

export const FormWrapper = styled.div`
  background-color: ${({ theme }) => theme.themeColors.white};
  border: 0.0625rem solid ${({ theme }) => theme.themeColors.darkGreen4};
  border-radius: 1.125rem;
  padding: 1.675rem 1.5rem;
  order: 2;
  width: 100%;

  ${({ theme }) => theme.mq.md`
    padding: 2rem 2rem 1.75rem;
  `}

  ${({ theme }) => theme.mq.lg`
    border-radius: 1.875rem;
    margin: 0 .5rem 0 0;
    max-width: 26.25rem;
    order: 1;
    padding: 2rem 2rem 1.675rem;
  `}

  ${({ theme }) => theme.mq.xl`
    margin: 0 2rem 0 0;
    max-width: 30rem;
    padding: 3rem 2rem 1.675rem;
  `}

  ${({ theme }) => theme.mq.xxl`
    margin: 0 3.25rem 0 0;
  `}
`;

export const ImageWrapper = styled.div`
  align-self: center;
  height: 13.25rem;
  margin: 0 auto 3rem;
  order: 1;
  padding: 0;
  position: relative;
  width: 100%;

  ${({ theme }) => theme.mq.md`
    height: 30rem;
    max-width: 42.5rem;
    margin: 0 auto 5.75rem;
  `}

  ${({ theme }) => theme.mq.lg`
    height: 28.75rem;
    margin: 2.4375rem 0 1.125rem 1.375rem;
    max-width: 48.25rem;
    order: 2;
  `}
`;

export const Title = styled.h2`
  color: ${({ theme }) => theme.themeColors.darkGreen1};
  letter-spacing: 0.033rem;
  margin-bottom: 1.25rem;
  margin-top: 0;
  text-align: left;

  div {
    font-family: ${({ theme }) => theme.fontStacks.serifItalic};
    font-style: italic;
  }

  br {
    display: none;
  }

  ${({ theme }) => theme.mq.md`
    font-size: 2.8125rem;
    margin-bottom: 2.5rem;

    br {
      display: block;
    }
    
    div {
      display: inline;
    }
  `}

  ${({ theme }) => theme.mq.lg`
      font-size: 2.5rem;

      div {
        display: block;
      }

      br {
        display: none;
      }
  `}

  ${({ theme }) => theme.mq.xl`
      font-size: 3rem;
      margin-bottom: 4.5rem;
  `}
`;

export const SearchForm = styled.form`
  display: grid;
  margin: 0 auto;
  row-gap: 1.125rem;
  width: 100%;
  column-gap: 1.25rem;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr 1fr 1fr 1fr;

  ${({ theme }) => theme.mq.md`
    row-gap: 1.375rem;
  `}

  ${({ theme }) => theme.mq.lg`
    row-gap: 3rem;
  `}
`;

export const SearchButton = styled(Button)`
  margin-top: 0.675rem;

  ${({ theme }) => theme.mq.lg`
    margin-top: -0.25rem;
  `}
`;

export const StyledAddressDropdown = styled(AddressDropdown)`
  svg {
    margin-top: 0.1875rem;
  }
`;

export const StyledMagIcon = styled(MagIcon)`
  margin-right: 0.2rem;
`;

export const StyledTextInput = styled(TextInput)``;

export const LineDivide = styled.hr`
  opacity: 0.65;
  border: none;
  background-color: ${({ theme }) => theme.themeColors.blue4};
  height: 0.0625rem;
`;

export const ListContainer = styled.div`
  margin: 0.625rem 0;
  border: 0.0625rem solid #eef5f5;
  box-sizing: border-box;
  box-shadow: 0 0.125rem 0.375rem rgba(138, 168, 181, 0.1253);
  position: absolute;
  width: calc(100% - 3.125rem);
  z-index: 1;
  padding: 0.4rem 0.3rem;
  max-width: 26.875rem;
  max-height: 21.875rem;
  overflow: scroll;
  overflow-x: hidden;
  background-color: white;
  border-radius: 0.75rem;
  &::-webkit-scrollbar {
    width: 0.5rem;
  }

  &::-webkit-scrollbar-track {
    margin: 0.9375rem;
    box-shadow: none;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 1.25rem;
    height: 7.125rem;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.15);
  }

  ${({ theme }) => theme.mq.lg`
    width: 100%;
  `}
`;

export const serviceTypeOptions = [
  {
    value: "all",
    label: "All Services",
  },
  {
    value: "grooming",
    label: "Grooming",
  },
  {
    value: "training",
    label: "Training",
  },
  {
    value: "veterinary",
    label: "Veterinary",
  },
];

const tomorrow = DateTime.now().plus({ days: 1 }).toJSDate();
const tomorrowMsecs = parseDateToMsecs(tomorrow);

function LandingPageSearchComponent({ theme, backgroundImage, title }) {
  const router = useRouter();
  const visible = router?.query?.visible;
  const address = useCurrentLocation();
  const { push } = useRouter();
  const vendorClient = useVendorClient();
  const [vendorBusinesses, setVendorBusinesses] = useState({});
  const [error, setError] = useState("");
  const handleLocationChange = useLocationChange();
  const businessDropdownRef = React.useRef(null);
  useOutsideClickHider([businessDropdownRef], () => {
    setVendorBusinesses(0);
  });

  const onChangeSearchHandler = (e) => {
    const searchString = e?.target.value ?? "";

    const dataParams = {
      data: {
        data: {
          vendorBusinesses: {
            fields: [
              "businessName",
              "formattedAddress",
              "vendorUserId",
              "logo",
            ],
            filter: {
              strictSearchByBusinessName: searchString,
              restricted: false,
            },
            include: {
              vendorServices: {
                fields: ["name"],
                filter: {
                  vendorBusinessId: { fromContext: "vendorBusiness.id" },
                },
              },
            },
            paginate: { number: 1, size: 12 },
          },
        },
      },
    };
    vendorClient
      .request({
        method: "get",
        url: `${BASE_API_URL}/deep-unrest/read`,
        params: dataParams,
      })
      .then(({ data }) => {
        if (error) {
          setError("");
        }
        // setLoading(false);
        setVendorBusinesses(data.vendorBusinesses);
      })
      .catch(() => {
        // setLoading(false);
        setError(
          "There appears to have been an error. Please reload page and try again."
        );
      });
  };

  const handleSearch = (values) => {
    const forward_address = values.location || address;
    const lat = get(forward_address, "latitude", undefined);
    const lng = get(forward_address, "longitude", undefined);

    const formattedAddress = get(
      forward_address,
      "formatted_address",
      undefined
    );
    push({
      pathname: "/search",
      query: {
        ...(visible && { visible }),
        ...(formattedAddress && { formattedAddress }),
        ...(lat && { latitude: lat }),
        ...(lng && { longitude: lng }),
        ...(values.s && { s: values.s }),
        ...(values.q && { q: values.q }),
        ...(values.startDateTime && { startDateTime: values.startDateTime }),
      },
    });
  };

  const debouncedChangeSearchHandler = useCallback(
    debounce(onChangeSearchHandler, 300),
    []
  );

  useEffect(() => {
    return () => {
      // Stop the invocation of the debounced function after unmounting
      debouncedChangeSearchHandler.cancel();
    };
  }, []);

  return (
    <OuterWrapper backgroundImage={backgroundImage}>
      <FormWrapper>
        <Title>{title}</Title>
        <Form
          onSubmit={handleSearch}
          initialValues={{
            s: "all",
            startDateTime: tomorrowMsecs,
            location: address,
          }}
          validate={(values) => {
            const errors = {};
            const forward_address = values.location?.formatted_address
              ? values.location
              : address;

            if (values.location === undefined) {
              errors.location = "Required";
            }
            if (!forward_address?.formatted_address) {
              errors.location = "Sorry, we couldn’t find that address.";
            }
            if (!forward_address?.latitude) {
              errors.location = "Sorry, we couldn’t find that address.";
            }
            if (!forward_address?.longitude) {
              errors.location = "Sorry, we couldn’t find that address.";
            }

            return errors;
          }}
          render={({ handleSubmit, submitting }) => {
            return (
              <SearchForm>
                <FormSpy
                  subscription={{ values: true }}
                  onChange={({ values: { location } }) => {
                    if (isObject(location)) {
                      handleLocationChange(location);
                    }
                  }}
                />
                <Field
                  name="location"
                  render={(props) => (
                    <Suspense>
                      <StyledAddressDropdown
                        isSearch
                        label="Location"
                        placeholder="Location"
                        LeadingIcon={
                          <LocationIcon color={theme.themeColors.darkGreen1} />
                        }
                        {...props}
                      />
                    </Suspense>
                  )}
                />
                <Field
                  name="s"
                  render={(props) => (
                    <Dropdown
                      highlightColor="green"
                      placeholder="Service"
                      options={serviceTypeOptions}
                      LeadingIcon={
                        <StorefrontIcon color={theme.themeColors.darkGreen1} />
                      }
                      {...props}
                    />
                  )}
                />
                <div>
                  <Field
                    name="q"
                    render={(props) => (
                      <TextInput
                        label="Search"
                        placeholder="Business Name (Optional)"
                        LeadingIcon={() => (
                          <StyledMagIcon color={theme.themeColors.darkGreen1} />
                        )}
                        {...props}
                        input={{
                          onChange: (e) => {
                            debouncedChangeSearchHandler(e);
                            props.input.onChange(e);
                          },
                        }}
                      />
                    )}
                  />
                  {vendorBusinesses && vendorBusinesses?.data?.length ? (
                    <ListContainer ref={businessDropdownRef}>
                      {vendorBusinesses?.data?.map((biz, i) => {
                        return (
                          <div>
                            <SmallBusinessCard
                              onClick={(e) => {
                                e.preventDefault();
                                push(`/business-redirect/${biz.id}`);
                              }}
                              vendorBusiness={biz}
                            />
                            {i < vendorBusinesses?.data?.length - 1 ? (
                              <LineDivide />
                            ) : null}
                          </div>
                        );
                      })}
                    </ListContainer>
                  ) : null}
                </div>
                <SearchButton
                  disabled={submitting || !address}
                  onClick={handleSubmit}
                  role="button"
                  type="submit"
                >
                  Search
                </SearchButton>
              </SearchForm>
            );
          }}
        />
      </FormWrapper>
      <ImageWrapper>
        <Image
          alt="Illustration of pets and parents playing in a park in front of a GoodFriend groomer"
          layout="fill"
          src="/hero_illustration.png"
          objectFit="contain"
          priority
        />
      </ImageWrapper>
    </OuterWrapper>
  );
}

export default withTheme(LandingPageSearchComponent);

LandingPageSearchComponent.propTypes = {
  input: PropTypes.shape({ onChange: PropTypes.func }),
  ipCityName: PropTypes.string,
  ipLatitude: PropTypes.number,
  ipLongitude: PropTypes.number,
  theme: PropTypes.shape({
    themeColors: PropTypes.shape({
      darkGreen1: PropTypes.string,
    }),
  }),
  backgroundImage: PropTypes.string,
  title: PropTypes.shape({}),
};

LandingPageSearchComponent.defaultProps = {
  input: {},
  ipCityName: undefined,
  ipLatitude: undefined,
  ipLongitude: undefined,
  theme: undefined,
  backgroundImage: "",
  title: undefined,
};
